import { useMutation } from '@apollo/client';
import { SEND_TEST_WEBHOOK } from 'app/graphql';
import { WebhookEventType } from 'app/webhooks/types';
import Dropdown from 'components/Dropdown';
import { Button } from 'components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { useState } from 'react';
import { toast } from 'sonner';

interface TestWebhookSenderProps {
  checkoutRequestId: string;
  developerId: string | undefined;
  orderDidPlace: boolean;
  webhookUrl: string | undefined;
}

export function TestWebhookSender({
  checkoutRequestId,
  developerId,
  orderDidPlace,
  webhookUrl,
}: TestWebhookSenderProps) {
  const [webhookValue, setWebhookValue] = useState(WebhookEventType.TrackingObtained);

  const [sendTestWebhook, { loading }] = useMutation(SEND_TEST_WEBHOOK, {
    context: { clientName: 'admin' },
  });

  let disabledReason: string | undefined = undefined;
  if (!orderDidPlace) {
    disabledReason =
      'Order did not complete successfully, so test webhooks cannot be sent. Try a different order.';
  } else if (!webhookUrl) {
    disabledReason = 'Add a webhook URL in the "Account" section.';
  }

  return (
    <div className="webhooks">
      <Dropdown
        options={Object.entries(WebhookEventType).map(([key, value]) => ({
          label: key,
          value,
        }))}
        value={webhookValue}
        onSelect={(value) => setWebhookValue(value)}
      />

      {developerId && (
        <Tooltip open={disabledReason ? undefined : false}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={loading || disabledReason !== undefined}
              onClick={() => {
                const isProductEvent = webhookValue.startsWith('PRODUCT_');

                sendTestWebhook({
                  variables: {
                    input: isProductEvent
                      ? {
                          orderProductEvent: {
                            orderId: checkoutRequestId,
                            type: webhookValue,
                          },
                        }
                      : {
                          orderEvent: {
                            orderId: checkoutRequestId,
                            type: webhookValue,
                          },
                        },
                  },
                })
                  .then(() => {
                    // TODO: Once we add a `webhook(id: ID!)` query, poll for final status
                    // using `result.data?.sendTestWebhook.id`.
                    toast.success('Webhook queued for delivery');
                  })
                  .catch((error) => {
                    toast.error(`Error: ${error.message}`);
                  });
              }}
              className={webhookUrl ? 'webhooks-send-button' : 'webhooks-send-button-disabled'}
            >
              Send
            </Button>
          </TooltipTrigger>
          <TooltipContent>{disabledReason}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
